import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Loading, Pagination, StorageCard, Trans } from '@/components'
import { useTeamStore } from '@/stores/team'

import { DataFilters } from '../components/DataFilters'
import { DataTable } from '../components/DataTable'
import { DataViewSwitcher } from '../components/DataViewSwitcher'
import { PAGE_SIZE } from '../constants'
import { useDataMemberList } from '../hooks/useDataMemberList'
import { useTeamFSSpaceStats } from '../hooks/useTeamFSSpaceStats'

export function AdminDataView() {
  const [selectedRoles, setSelectedRoles] = useState<number[]>([])

  const teamStore = useTeamStore()
  const teamTokenData = teamStore.teamTokenData

  // Fetch team FS space statistics (team shared + all members)
  const { teamShared, allMembers } = useTeamFSSpaceStats({
    teamResID: teamTokenData?.teamResID,
  })

  // Fetch member list with role filter
  const { members, loading, page, total, fetchMembers } = useDataMemberList({
    teamResID: teamTokenData?.teamResID,
    selectedRoles,
  })

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Title + ViewSwitcher */}
      <div className="border-b border-neutral-100 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h6">
              <Trans i18nKey="teamData.admin.title">Datadrive</Trans>
            </div>
            <DataViewSwitcher />
          </div>
        </div>
      </div>

      {/* Section 2: Storage Overview Cards */}
      <div className="grid grid-cols-2 gap-4">
        <StorageCard
          title={<Trans i18nKey="teamData.admin.teamCardTitle">Team Datadrive</Trans>}
          usage={teamShared.usageBytes}
          quota={teamShared.quotaBytes}
          colorVariant="tertiary"
          legendText={<Trans i18nKey="teamData.admin.usedSpace">Used Space</Trans>}
          actions={
            <Link to="/team/data/shared" className="text-highlight t-sb">
              <Trans i18nKey="teamData.admin.manageLink">Manage</Trans>
            </Link>
          }
        />
        <StorageCard
          title={
            <Trans i18nKey="teamData.admin.allMembersCardTitle">Team Datadrive (All Members)</Trans>
          }
          usage={allMembers.usageBytes}
          quota={allMembers.quotaBytes}
          colorVariant="secondary"
          legendText={<Trans i18nKey="teamData.admin.usedSpace">Used Space</Trans>}
        />
      </div>

      {/* Section 3: Filters + Table */}
      <div className="flex flex-col gap-4">
        {/* Filters */}
        <DataFilters selectedRoles={selectedRoles} onRolesChange={setSelectedRoles} />

        {/* Table */}
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <DataTable members={members} />
            <div className="flex justify-end items-center mt-4">
              <Pagination
                size={PAGE_SIZE}
                total={total}
                current={page}
                onChange={(newPage) => fetchMembers(newPage)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
